import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { conversations as convApi } from "../lib/api";
import { MessageCircle, Send, ArrowLeft, Plus, Store, User, Loader2, X } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86400)} j`;
};

const Avatar = ({ src, name, shop }) => (
  <div className="w-10 h-10 rounded-full bg-[#252525] overflow-hidden shrink-0 flex items-center justify-center border border-[#333]">
    {src ? <img src={src} alt="" className="w-full h-full object-cover" /> :
      shop ? <Store className="w-5 h-5 text-[#FF6B35]" /> :
        <span className="text-[#FF6B35] font-bold">{(name || "?").charAt(0).toUpperCase()}</span>}
  </div>
);

export default function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [contacts, setContacts] = useState({ friends: [], shops: [] });
  const lastTs = useRef("");
  const pollRef = useRef(null);
  const scrollRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const r = await convApi.list();
      setConvs(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ }
    finally { setLoadingList(false); }
  }, []);

  const openConversation = useCallback(async (participantId) => {
    try {
      const r = await convApi.open(participantId);
      const id = r.data?.id;
      await loadConvs();
      if (id) setActiveId(id);
    } catch { /* silent */ }
  }, [loadConvs]);

  // Initial load + handle ?to= / ?c=
  useEffect(() => {
    const to = params.get("to");
    const c = params.get("c");
    (async () => {
      await loadConvs();
      if (to) {
        await openConversation(to);
      } else if (c) {
        setActiveId(c);
      }
      if (to || c) setParams({}, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMessages = useCallback(async (id, initial) => {
    if (!id) return;
    if (initial) { setLoadingMsgs(true); lastTs.current = ""; }
    try {
      const r = await convApi.messages(id, initial ? undefined : lastTs.current);
      const list = Array.isArray(r.data) ? r.data : [];
      if (initial) setMessages(list);
      else if (list.length) setMessages((prev) => [...prev, ...list]);
      if (list.length) lastTs.current = list[list.length - 1].created_at;
    } catch { /* silent */ }
    finally { if (initial) setLoadingMsgs(false); }
  }, []);

  // Load messages + poll when active conversation changes
  useEffect(() => {
    if (!activeId) return;
    fetchMessages(activeId, true);
    pollRef.current = setInterval(() => fetchMessages(activeId, false), 5000);
    return () => clearInterval(pollRef.current);
  }, [activeId, fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !activeId || sending) return;
    setSending(true);
    setText("");
    try {
      const r = await convApi.send(activeId, t);
      setMessages((prev) => [...prev, r.data]);
      lastTs.current = r.data.created_at;
      loadConvs();
    } catch { setText(t); }
    finally { setSending(false); }
  };

  const openContacts = async () => {
    setShowContacts(true);
    try {
      const r = await convApi.contacts();
      setContacts({ friends: r.data?.friends || [], shops: r.data?.shops || [] });
    } catch { setContacts({ friends: [], shops: [] }); }
  };

  const startWith = async (c) => {
    setShowContacts(false);
    await openConversation(c.id);
  };

  const active = convs.find((c) => c.id === activeId);
  const activeOther = active?.other || {};
  const contactList = [...(contacts.friends || []).map((f) => ({ ...f, is_shop: false })), ...(contacts.shops || [])];

  return (
    <div className="max-w-[1100px] mx-auto" data-testid="messages-page">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-6 h-6 text-[#4ECDC4]" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">Messagerie</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
        {/* Conversation list */}
        <aside className={`md:col-span-1 bg-[#141414] border border-[#333] rounded-xl overflow-hidden flex flex-col ${activeId ? "hidden md:flex" : "flex"}`} data-testid="conversation-list">
          <div className="p-3 border-b border-[#333] flex items-center justify-between">
            <span className="text-white font-semibold text-sm">Conversations</span>
            <button onClick={openContacts} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#FF6B35] hover:bg-[#E55A25] text-white text-xs font-semibold transition-colors" data-testid="new-message-btn">
              <Plus className="w-3.5 h-3.5" />Nouveau
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-[#4ECDC4] animate-spin" /></div>
            ) : convs.length === 0 ? (
              <div className="px-4 py-10 text-center text-[#666] text-sm">
                Aucune conversation.<br />Clique sur « Nouveau » pour écrire à un ami ou une boutique suivie.
              </div>
            ) : convs.map((c) => (
              <button key={c.id} onClick={() => setActiveId(c.id)} className={`w-full flex items-center gap-3 px-3 py-3 text-left border-b border-[#222] hover:bg-[#1e1e1e] transition-colors ${activeId === c.id ? "bg-[#1e1e1e]" : ""}`} data-testid={`conversation-item-${c.id}`}>
                <Avatar src={c.other?.avatar_url} name={c.other?.pseudo || c.other?.name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white text-sm font-medium truncate">{c.other?.pseudo || c.other?.name || "Utilisateur"}</span>
                    <span className="text-[#555] text-[10px] shrink-0">{timeAgo(c.last_at)}</span>
                  </div>
                  <p className="text-[#888] text-xs truncate mt-0.5">{c.last_message || "…"}</p>
                </div>
                {c.unread > 0 && <span className="w-2.5 h-2.5 rounded-full bg-[#4ECDC4] shrink-0" data-testid="conv-unread-dot" />}
              </button>
            ))}
          </div>
        </aside>

        {/* Thread */}
        <section className={`md:col-span-2 bg-[#141414] border border-[#333] rounded-xl overflow-hidden flex flex-col ${activeId ? "flex" : "hidden md:flex"}`} data-testid="message-thread">
          {!activeId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-[#666] px-6">
              <MessageCircle className="w-12 h-12 text-[#333] mb-3" />
              <p className="text-sm">Sélectionne une conversation pour commencer à discuter.</p>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-[#333] flex items-center gap-3">
                <button onClick={() => setActiveId(null)} className="md:hidden p-1 text-[#888] hover:text-white" data-testid="thread-back-btn"><ArrowLeft className="w-5 h-5" /></button>
                <Avatar src={activeOther.avatar_url} name={activeOther.pseudo || activeOther.name} />
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{activeOther.pseudo || activeOther.name || "Utilisateur"}</p>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-scroll">
                {loadingMsgs ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-[#4ECDC4] animate-spin" /></div>
                ) : messages.length === 0 ? (
                  <p className="text-center text-[#666] text-sm py-10">Aucun message. Dis bonjour 👋</p>
                ) : messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="message-bubble">
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#FF6B35] text-white rounded-br-sm" : "bg-[#252525] text-[#eee] rounded-bl-sm"}`}>
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[9px] mt-1 ${mine ? "text-white/60" : "text-[#666]"}`}>{timeAgo(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={handleSend} className="p-3 border-t border-[#333] flex items-center gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Écris un message..."
                  className="flex-1 bg-[#1e1e1e] border border-[#333] rounded-full px-4 py-2.5 text-sm text-white outline-none focus:border-[#FF6B35] transition-colors"
                  data-testid="message-input"
                />
                <button type="submit" disabled={!text.trim() || sending} className="w-10 h-10 rounded-full bg-[#FF6B35] hover:bg-[#E55A25] disabled:opacity-40 flex items-center justify-center text-white transition-colors" data-testid="send-message-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </section>
      </div>

      {/* Contacts modal */}
      {showContacts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowContacts(false)} data-testid="contacts-modal">
          <div className="bg-[#1a1a1a] border border-[#333] rounded-xl w-full max-w-sm max-h-[70vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-[#333] flex items-center justify-between">
              <span className="text-white font-semibold text-sm">Nouveau message</span>
              <button onClick={() => setShowContacts(false)} className="p-1 text-[#888] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {contactList.length === 0 ? (
                <div className="px-4 py-8 text-center text-[#666] text-sm">
                  Aucun contact pour le moment.<br />Suis une boutique ou ajoute des amis, puis écris-leur depuis leur profil (bouton « Message »).
                </div>
              ) : (
                <>
                  {contacts.shops?.length > 0 && <p className="text-[10px] text-[#666] uppercase font-semibold px-2 py-1.5">Boutiques suivies</p>}
                  {contacts.shops?.map((c) => (
                    <button key={`s-${c.id}`} onClick={() => startWith(c)} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-[#252525] transition-colors text-left" data-testid={`contact-${c.id}`}>
                      <Avatar src={c.avatar_url} name={c.name} shop />
                      <span className="text-white text-sm truncate">{c.pseudo || c.name}</span>
                    </button>
                  ))}
                  {contacts.friends?.length > 0 && <p className="text-[10px] text-[#666] uppercase font-semibold px-2 py-1.5 mt-1">Amis</p>}
                  {contacts.friends?.map((c) => (
                    <button key={`f-${c.id}`} onClick={() => startWith(c)} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-[#252525] transition-colors text-left" data-testid={`contact-${c.id}`}>
                      <Avatar src={c.avatar_url} name={c.pseudo || c.name} />
                      <span className="text-white text-sm truncate">{c.pseudo || c.name}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
